import React, { useContext, useEffect, useState } from "react";

import { Grid, IconButton, Tooltip } from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

import { UserContext } from "App";

import { useDispatch } from "react-redux";

import { GetBlogDetails } from "APIs/BlogAPIs";

import { useParams, useNavigate } from "react-router-dom";

// Receipt page theme (shared)
import SectionCard, {
  SectionHeader,
  ranuOutlinedButtonSx,
  useAccent,
} from "layouts/receipt/components/SectionCard";

import FormField from "layouts/receipt/components/FormField";
import { Loader } from "components/Custom/Loader";

// Thumbnail frame. `isNew` = image selected but not uploaded yet (gold border).
const tileSx =
  (isNew = false) =>
  ({ palette, borders }) => ({
    position: "relative",
    display: "block",
    width: "100%",
    height: 140,
    overflow: "hidden",
    textDecoration: "none",
    borderRadius: borders.borderRadius.lg,
    backgroundColor: palette.light.main,
    border: isNew
      ? `2px solid ${palette.secondary.main}`
      : `${borders.borderWidth[1]} solid ${borders.borderColor}`,
  });
// Hover: golden background + white text (border and icon follow the text colour)
const goldHoverSx = ({ palette }) => ({
  "&:hover": {
    backgroundColor: palette.secondary.main,
    borderColor: palette.secondary.main,
    color: palette.white.main,
    boxShadow: "none",
  },
});

// Same look as a disabled MDInput (grey background, faded text), but the field stays
// scrollable. MDInput sets `pointer-events: none` on disabled fields, so a long
// description could not be scrolled with the mouse / touch.
const readOnlyLookSx = ({ palette }) => ({
  backgroundColor: `${palette.grey[200]} !important`,
  "& .MuiInputBase-input": {
    color: palette.text.disabled,
    WebkitTextFillColor: palette.text.disabled,
    overflowY: "auto !important",
  },
});

const ViewBlog = () => {
  const userData = useContext(UserContext);

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const { id } = useParams();

  const { accentName } = useAccent();

  const [blogDetails, setBlogDetails] = useState(null);
  const [isFetchingData, setIsFetchingData] = useState(true);

  const getBlogDetailsFunc = async (blogId, access_token, dispatchFunc) => {
    setIsFetchingData(true);
    const response = await GetBlogDetails(blogId, access_token, dispatchFunc);

    if (response !== null) {
      setBlogDetails(response);
    }
    setIsFetchingData(false);
  };

  useEffect(() => {
    if (userData?.access_token !== undefined) {
      getBlogDetailsFunc(id, userData.access_token, dispatch);
    }
  }, [userData, id]);

  const formatDescription = (text) => {
    if (!text) return "No description provided.";

    return text.replace(/\\n/g, "\n");
  };

  const imageCount = blogDetails?.image_list?.length || 0;

  return (
    <MDBox px={{ xs: 1, md: 3 }} pt={{ xs: 0, md: 1 }} pb={3}>
      <SectionHeader
        icon="visibility"
        label="VIEW BLOG"
        startAction={
          <MDButton
            variant="outlined"
            color={accentName}
            size="medium"
            sx={ranuOutlinedButtonSx}
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/blog")}
          >
            Back
          </MDButton>
        }
        endAction={
          <MDBox
            sx={{
              display: { xs: "none", md: "block" },
            }}
          >
            <MDButton
              color="primary"
              size="medium"
              sx={[ranuOutlinedButtonSx, goldHoverSx]}
              startIcon={<EditIcon />}
              onClick={() => navigate(`/blog/edit/${id}`)}
            >
              Edit Blog
            </MDButton>
          </MDBox>
        }
      />
      {isFetchingData ? (
        <Loader />
      ) : (
        <>
          {/* ------------------------------ Details ------------------------------ */}
          <SectionCard title="Details">
            <Grid container spacing={2}>
              <FormField name="title" full>
                <MDInput disabled fullWidth value={blogDetails?.blog_title || ""} />
              </FormField>

              <FormField name="description" full>
                <MDInput
                  fullWidth
                  multiline
                  minRows={4}
                  maxRows={14}
                  value={formatDescription(blogDetails?.blog_description)}
                  InputProps={{ readOnly: true }}
                  sx={readOnlyLookSx}
                />
              </FormField>
            </Grid>
          </SectionCard>

          {/* ------------------------------- Media ------------------------------- */}
          <SectionCard
            title={`Media (${imageCount})`}
            subtitle="Click any image to open it in a new tab"
          >
            <Grid container spacing={2}>
              {blogDetails?.image_list && blogDetails.image_list.length > 0 ? (
                blogDetails.image_list.map((img) => (
                  <Grid item xs={6} sm={4} md={3} key={img.blog_image_id}>
                    <MDBox
                      component="a"
                      href={img.media}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={tileSx(false)}
                    >
                      <img
                        src={img.media}
                        alt={img.image}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />

                      <Tooltip title="Open image in new tab">
                        <IconButton
                          size="small"
                          style={{
                            position: "absolute",
                            top: 6,
                            right: 6,
                            backgroundColor: "rgba(0, 0, 0, 0.65)",
                            color: "#ffffff",
                            padding: "4px",
                          }}
                        >
                          <OpenInNewIcon style={{ fontSize: "16px" }} />
                        </IconButton>
                      </Tooltip>
                    </MDBox>
                  </Grid>
                ))
              ) : (
                <Grid item xs={12}>
                  <MDTypography variant="button" color="text">
                    No images attached.
                  </MDTypography>
                </Grid>
              )}
            </Grid>
          </SectionCard>

          {/* Mobile Edit Button */}
          <MDBox
            sx={{
              display: { xs: "flex", md: "none" },
              justifyContent: "center",
              mt: 2,
            }}
          >
            <MDButton
              color="primary"
              size="medium"
              sx={[ranuOutlinedButtonSx, goldHoverSx]}
              startIcon={<EditIcon />}
              onClick={() => navigate(`/blog/edit/${id}`)}
            >
              Edit Blog
            </MDButton>
          </MDBox>
        </>
      )}
    </MDBox>
  );
};

export default ViewBlog;
